// src/helpers/config-utilities.js

const PAIR_DELIMITER = "_";
const KV_DELIMITER = "-";
export const CONFIG_CODE_KEY = "configCode"; // Key used for the URL search parameter

/**
 * Creates a path-friendly configuration string, ensuring incompatible options
 * (CaEx/CaIn) are resolved and all valid options (like SpAc) are included.
 * Format: [GroupKey]-[DescriptorValue]_[GroupKey]-[DescriptorValue]
 * e.g., ExPt-prm_WhMo-kpr_FrLi-wcf
 * @param {object} selectedMap - The current map of selected options.
 * @returns {string} The URL-encoded configuration code string.
 */
export function createUrlConfigString(selectedMap) {
  const tempMap = {};
  // 1. Filter and normalize options, collecting all valid key/value pairs
  for (const groupKey in selectedMap) {
    if (!selectedMap.hasOwnProperty(groupKey) || groupKey === "misc") continue;

    const option = selectedMap[groupKey];

    // Only include valid group keys (Mod, Do)
    const VALID_GROUP_KEYS = ["Mod", "Do"];
    if (!VALID_GROUP_KEYS.includes(groupKey)) continue;

    // Safely read descriptor value
    const descriptorValue = option?.descriptor?.[groupKey];

    if (descriptorValue !== undefined && descriptorValue !== null) {
      tempMap[groupKey] = descriptorValue;
    }
  }

  // 3. Build the final URL string from the filtered/fixed map (tempMap)
  const params = [];
  const sortedKeys = Object.keys(tempMap).sort();

  for (const groupKey of sortedKeys) {
    let descriptorValue = tempMap[groupKey];

    // --- CONVERSION FOR URL ENCODING (PRE-PROCESSING) ---
    // Handle booleans: convert true/false to string 'true'/'false'
    if (typeof descriptorValue === "boolean") {
      descriptorValue = String(descriptorValue);
    }
    // Handle numbers: convert to string
    if (typeof descriptorValue === "number") {
      descriptorValue = String(descriptorValue);
    }
    // ---------------------------------------------------

    const key = encodeURIComponent(groupKey);
    const value = encodeURIComponent(descriptorValue);

    // Format: Key-Value
    params.push(`${key}${KV_DELIMITER}${value}`);
  }

  // 4. Join pairs
  return params.join(PAIR_DELIMITER);
}

/**
 * Parses a path configuration string back into a flat descriptor map.
 * @param {string} configPathString - The URL configuration code (e.g., ExPt-prm_WhMo-kpr...).
 * @returns {object} The descriptor map with correctly typed values.
 */
export function parseUrlConfigString(configPathString) {
  const descriptorMap = {};
  const pairs = configPathString.split(PAIR_DELIMITER);

  for (const pair of pairs) {
    if (!pair) continue;
    // Split key and value by the KV_DELIMITER
    const [key, value] = pair.split(KV_DELIMITER);

    if (key && value) {
      const groupKey = decodeURIComponent(key);
      let descriptorValue = decodeURIComponent(value);

      // --- CRITICAL FIX: TYPE CONVERSION (POST-PROCESSING) ---

      // 1. Convert string 'true'/'false' back to boolean type
      if (descriptorValue === "true") {
        descriptorValue = true;
      } else if (descriptorValue === "false") {
        descriptorValue = false;
      }
      // 2. Convert string to number type if it looks like a number (e.g., '0.5' for sliders)
      else if (!isNaN(descriptorValue) && descriptorValue.trim() !== "") {
        // Use parseFloat to handle decimals (like "0.5")
        const numValue = parseFloat(descriptorValue);
        // Only use the numeric value if it's a valid number
        if (!isNaN(numValue)) {
          descriptorValue = numValue;
        }
      }
      // ---------------------------------------------------

      // Store the final, correctly typed value
      descriptorMap[groupKey] = descriptorValue;
    }
  }

  return descriptorMap;
}

/**
 * Combines the base URL and the configuration string into a complete,
 * shareable URL using a search parameter.
 * e.g., 'http://localhost:3000/?configCode=ExPt-prm_WhMo-kpr...'
 *
 * @param {string} configString - The configuration code generated by createUrlConfigString.
 * @returns {string} The full shareable URL.
 */
export function createShareableUrl() {
  // 1. Get the current base URL (protocol, hostname, port)
  // We only take the origin (protocol + host) to ensure we start from the root path
  const url = window?.location?.href;
  return url;
}
